import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ShoppingCart, User, Grid, List, ChevronDown, Phone, Shield, Clock } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import SearchBar from '../components/SearchBar';
import SEOHead from '../components/SEO/SEOHead';
import Navigation from '../components/Navigation';
import { FairViewsCombo } from '../components/FairViewsWidgets';
import { products, brands, navCategories } from '../mockData';
import axios from 'axios';
import Logo from '../components/Logo';
import { useCart } from '../context/CartContext';
import { useBrand } from '../components/BrandProvider';

// Textes synchronisés avec la vidéo hero (15 secondes en boucle)
// hasLink indicates if this slide should link to a dedicated page
const heroTexts = [
  { start: 0, end: 2.5, line1: "Atelier français.", line2: "Confection à la main.", hasLink: false },
  { start: 2.5, end: 5.0, line1: "Service Chronos™ : fabrication en 24h", line2: "* Jours ouvrés • avant 9h • toiles CHRONOS™", hasLink: true, linkTo: "/service-chronos", icon: "clock" },
  { start: 5.0, end: 7.5, line1: "Commande avant 9h : expédition à J+1", line2: "* Jours ouvrés • toiles CHRONOS™", hasLink: true, linkTo: "/service-chronos", icon: "clock" },
  { start: 7.5, end: 10.5, line1: "Accès \"coupe-file\" en production", line2: "* Jours ouvrés • avant 9h • toiles CHRONOS™", hasLink: true, linkTo: "/service-chronos", icon: "clock" },
  { start: 10.5, end: 12.5, line1: "Garantie prise de mesures incluse", line2: "** Voir conditions", hasLink: true, linkTo: "/assurance-mesure", icon: "shield" },
  { start: 12.5, end: 15.0, line1: "ToileStoreBanne.com", line2: "Le sur-mesure, sans stress.", hasLink: false },
];

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cartItems } = useCart();
  const brandConfig = useBrand();
  const videoRef = useRef(null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  
  // Calculer le nombre total d'articles (somme des quantités)
  const totalItemsCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null); // Sera défini à la première collection chargée
  const [collections, setCollections] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [realProducts, setRealProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Récupérer la collection depuis l'URL si présente
  const urlCollection = searchParams.get('collection');

  // Synchronisation des textes avec la vidéo (un seul mécanisme propre)
  useEffect(() => {
    // Durées en millisecondes pour chaque texte
    const durations = [
      2500,  // 0-2.5s
      2500,  // 2.5-5s
      2500,  // 5-7.5s
      3000,  // 7.5-10.5s
      2000,  // 10.5-12.5s
      2500,  // 12.5-15s
    ];
    
    let timeoutId;
    let currentIdx = 0;
    
    const showNextText = () => {
      setCurrentTextIndex(currentIdx);
      timeoutId = setTimeout(() => {
        currentIdx = (currentIdx + 1) % heroTexts.length;
        showNextText();
      }, durations[currentIdx]);
    };
    
    // Démarrer la séquence
    showNextText();
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Charger les collections depuis l'API et définir la première comme sélectionnée
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/collections/`);
        const collectionsData = response.data || [];
        setCollections(collectionsData);
        
        // Si une collection est spécifiée dans l'URL, la sélectionner
        if (urlCollection) {
          const matchingCollection = collectionsData.find(
            c => c.name === urlCollection || c.name.toLowerCase().includes(urlCollection.toLowerCase())
          );
          if (matchingCollection) {
            setSelectedCollection(matchingCollection.name);
            return;
          }
        }
        
        // Sinon, définir la première collection comme sélectionnée par défaut
        if (collectionsData.length > 0 && !selectedCollection) {
          setSelectedCollection(collectionsData[0].name);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des collections:', error);
        setCollections([]);
      }
    };
    
    fetchCollections();
  }, [urlCollection]);

  // Fonction pour gérer la navigation des catégories
  const handleCategoryClick = (categoryName) => {
    const routes = {
      "Toile De Store": "/",
      "Toile de Store Droit": "/store-droit",
      "Toile de Store à Double Pente": "/store-double-pente",
      "Lambrequin Seul": "/lambrequin",
      "Toile De Pergola": "/pergola",
      "Brise-Vue De Balcon": "/brise-vue",
      "Toile de Store au Metre": "/toile-au-metre"
    };
    
    const route = routes[categoryName];
    if (route) {
      navigate(route);
    }
  };

  // Charger les fournisseurs depuis l'API
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/suppliers/`);
        setSuppliers(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des fournisseurs:', error);
        setSuppliers([]);
      }
    };
    
    fetchSuppliers();
  }, []);

  // Charger les produits depuis l'API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/products/`);
        
        // Adapter les produits au format attendu par le frontend
        // Ne pas utiliser de placeholder - laisser le skeleton gérer l'affichage
        const adaptedProducts = response.data.map(product => ({
          id: product.id,
          name: product.ref_frs || product.code1 || product.full_code || 'Produit',
          collection: product.gamme || 'N/A',
          brand: product.supplier || 'N/A',
          price: 0, // Le prix sera calculé dynamiquement
          image: product.image || null,
          slug: product.slug || product.id,
          thumbnail: product.thumbnail || product.image || null,
          // Autres champs si nécessaire
          supplier: product.supplier,
          category: product.category,
          code1: product.code1,
          ref_frs: product.ref_frs
        }));
        
        setRealProducts(adaptedProducts);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    // Utiliser uniquement les vrais produits (pas de fallback vers mock)
    if (realProducts.length === 0) return [];
    let filtered = [...realProducts];

    // Filtrer par collection
    if (selectedCollection) {
      filtered = filtered.filter(p => p.collection === selectedCollection);
    }

    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    if (selectedColors.length > 0) {
      filtered = filtered.filter(p => selectedColors.includes(p.color));
    }

    if (selectedPatterns.length > 0) {
      filtered = filtered.filter(p => selectedPatterns.includes(p.pattern));
    }

    // Sort
    switch(sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }

    return filtered;
  }, [selectedCollection, selectedBrands, selectedColors, selectedPatterns, sortBy, realProducts]);

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Head avec métadonnées optimisées */}
      <SEOHead 
        title={`${brandConfig.name} - Toiles sur mesure pour stores bannes`}
        description={brandConfig.seo.defaultDescription}
        url="/"
        breadcrumbs={[
          { name: 'Accueil', url: '/' }
        ]}
      />
      
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-8">
            {/* Logo */}
            <Logo showTagline={true} clickable={true} />

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl">
              <SearchBar 
                placeholder="Rechercher une toile, référence, couleur..."
                showButton={true}
                selectedCollection={selectedCollection}
              />
            </div>

            {/* Phone, User & Cart */}
            <div className="flex items-center gap-4">
              {/* Phone Number */}
              <a 
                href={`tel:${brandConfig.contact.phone}`}
                className="hidden md:flex items-center gap-2 bg-celadon/10 hover:bg-celadon/20 text-celadon px-4 py-2 rounded-lg transition-colors"
              >
                <Phone size={20} className="animate-pulse" />
                <span className="font-bold text-lg">{brandConfig.contact.phoneDisplay}</span>
              </a>
              
              <button 
                onClick={() => navigate('/pro/login')}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
                title="Espace PRO"
              >
                <User size={24} />
              </button>
              <button 
                onClick={() => navigate('/panier')}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 relative"
              >
                <ShoppingCart size={24} />
                {totalItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-celadon text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {totalItemsCount}
                  </span>
                )}
                <span className="font-medium">PANIER</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Component avec callback pour filtrage */}
      <Navigation onCategoryClick={handleCategoryClick} />

      {/* Hero Section avec Vidéo */}
      <section className="relative w-full h-[500px] overflow-hidden bg-gradient-to-r from-celadon to-gray-700">
        {/* Vidéo de fond */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="https://customer-assets.emergentagent.com/job_product-sync-12/artifacts/1y53iu5v_HERO3.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay sombre pour améliorer la lisibilité du texte */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Contenu texte animé synchronisé avec la vidéo */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <div className="max-w-4xl">
            {/* Contenu avec lien conditionnel */}
            {heroTexts[currentTextIndex].hasLink ? (
              <Link 
                to={heroTexts[currentTextIndex].linkTo}
                className="block hover:scale-105 transition-transform cursor-pointer"
              >
                {/* Ligne 1 - Plus grande */}
                <h2 
                  key={`line1-${currentTextIndex}`}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 animate-fade-in drop-shadow-lg flex items-center justify-center gap-3"
                >
                  {heroTexts[currentTextIndex].icon === 'clock' ? (
                    <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-orange-400" />
                  ) : (
                    <Shield className="w-8 h-8 sm:w-10 sm:h-10" />
                  )}
                  {heroTexts[currentTextIndex].line1}
                </h2>
                {/* Ligne 2 - Plus petite (conditions) */}
                <p 
                  key={`line2-${currentTextIndex}`}
                  className="text-base sm:text-lg lg:text-xl font-normal animate-fade-in-delay drop-shadow-md opacity-80"
                >
                  {heroTexts[currentTextIndex].line2}
                </p>
                <p className="text-sm mt-3 opacity-75 animate-fade-in-delay underline">Cliquez pour en savoir plus</p>
              </Link>
            ) : (
              <>
                {/* Ligne 1 - Plus grande */}
                <h2 
                  key={`line1-${currentTextIndex}`}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 animate-fade-in drop-shadow-lg"
                >
                  {heroTexts[currentTextIndex].line1}
                </h2>
                {/* Ligne 2 - Plus petite */}
                <p 
                  key={`line2-${currentTextIndex}`}
                  className="text-xl sm:text-2xl lg:text-3xl font-medium animate-fade-in-delay drop-shadow-md"
                >
                  {heroTexts[currentTextIndex].line2}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles pour l'animation des textes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
        }
        .animate-fade-in-delay {
          animation: fadeIn 0.6s ease-out 0.15s forwards;
          opacity: 0;
        }
      `}</style>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="text-sm text-gray-600">
            <span className="hover:text-gray-900 cursor-pointer">Accueil</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900">Configurez votre toile de store banne sur-mesure</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <Sidebar 
            selectedBrands={selectedBrands}
            setSelectedBrands={setSelectedBrands}
            selectedColors={selectedColors}
            setSelectedColors={setSelectedColors}
            selectedPatterns={selectedPatterns}
            setSelectedPatterns={setSelectedPatterns}
            selectedCollection={selectedCollection}
            setSelectedCollection={setSelectedCollection}
            collections={collections}
          />

          {/* Main Content */}
          <div className="flex-1">
            {/* Page Title */}
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Configurez votre toile de store banne sur-mesure</h1>

            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <List size={20} />
                </button>
                {loading ? (
                  <span className="text-sm text-gray-600">Chargement des produits...</span>
                ) : (
                  <span className="text-sm text-gray-600">Il y a {filteredProducts.length} produits.</span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Trier par:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-celadon"
                >
                  <option value="relevance">Pertinence</option>
                  <option value="name-asc">Nom, A à Z</option>
                  <option value="name-desc">Nom, Z à A</option>
                  <option value="price-asc">Prix, croissant</option>
                  <option value="price-desc">Prix, décroissant</option>
                </select>
              </div>
            </div>

            {/* Products */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Chargement des produits...</p>
              </div>
            ) : (
              <ProductGrid 
                products={filteredProducts} 
                viewMode={viewMode}
                selectedCollectionName={collections.find(c => c.id === selectedCollection)?.name || selectedCollection}
              />
            )}
          </div>
        </div>
      </div>

      {/* FairViews Combo - Section témoignages clients */}
      <FairViewsCombo />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;